import { labelForService } from '@/components/organisms/org-admin/usageFormat';
import { rosterAllowance } from '../allowance';
import { NEAR_LIMIT_UTILISATION } from '../usageThresholds';
import { isMonthSelection } from '../types';
import type {
  OrgServiceUsageReport,
  OrgUsageReport,
  OrgUsageTimeseries,
  UsageWindowDays,
  UsageWindowSelection,
} from '../types';
import type {
  AttentionItem,
  CumulativeSpendPoint,
  SpendDecile,
  PersonSpendRow,
  ServiceMetricRow,
  SpendComparison,
  SpendSummary,
  UsageAnalysis,
} from './types';

/**
 * The single seam between the org-admin REST payloads and the Analysis view.
 *
 * Everything the view needs is derived here: unit reconciliation, the derived figures the API does
 * not send (budget totals, remaining, projection, period-over-period change, failure rates, shares
 * of total), display labels, sort order, the attention list, and the provenance record. Components
 * receive finished numbers.
 *
 * Three API facts are absorbed here rather than in components:
 *
 *   - **Three clocks.** `spend_usd_window` is scoped to the selected day window; `spend_usd_cycle` is
 *     scoped to the budget cycle and is the one LiteLLM enforces `budget_usd` against;
 *     `spend_usd_lifetime` is all-time and survives a reset. Everything about budget health uses
 *     `spend_usd_cycle` — pairing `budget_usd` with either of the others produces a utilisation figure
 *     that is wrong and entirely plausible.
 *   - **Two sources.** Spend/tokens come from LiteLLM; runs/failures/latency from observability. Each
 *     measure is sourced from exactly one and never summed across the two.
 *   - **No time dimension.** Neither source buckets by day — see `trendFixture.ts`.
 */

/**
 * The window whose total contains the one being viewed, used to derive the preceding stretch.
 * 30 has no larger sibling — the API allowlists only 7/15/30 — so it cannot be compared.
 */
export const COMPARISON_WINDOW: Record<UsageWindowDays, UsageWindowDays | null> = {
  7: 15,
  15: 30,
  30: null,
};

/**
 * A projection is only offered once enough of the cycle has elapsed to mean anything. At 8% elapsed
 * a single large run extrapolates to a terrifying, meaningless number.
 */
const MIN_ELAPSED_FRACTION_FOR_PROJECTION = 0.15;

/** Share of total spend above which one service is worth calling out by name. */
const DOMINANT_SERVICE_SHARE = 0.4;

/** `failed / runs`, or `null` — 0 runs is "no data", not a 0% failure rate. */
function rate(failed: number, runs: number): number | null {
  return runs > 0 ? failed / runs : null;
}

function safeShare(part: number, total: number): number {
  return total > 0 ? part / total : 0;
}

// ── Spend summary ───────────────────────────────────────────────────────────

function buildSpendSummary(report: OrgUsageReport | undefined, now: Date): SpendSummary {
  const members = report?.members ?? [];
  const cycleUsd = report?.org_total.spend_usd_cycle ?? 0;
  const windowUsd = report?.org_total.spend_usd ?? 0;

  // The org's budget is the sum of its seats' budgets — there is no org-level budget field. Seats
  // with no budget contribute 0, so an org with no budgets set reports 0 and utilisation stays null.
  const budgetUsd = members.reduce((sum, member) => sum + member.budget_usd, 0);

  /*
   * The cycle reset is per seat but is set org-wide in practice. The LATEST reset is used rather than
   * an average: it is the date by which every seat has definitely rolled over, so "days left" never
   * promises a reset that has not happened for everyone yet.
   */
  const resets = members
    .map((member) => (member.resets_at ? Date.parse(member.resets_at) : NaN))
    .filter((value) => !Number.isNaN(value));
  const cycleResetsAtMs = resets.length ? Math.max(...resets) : null;

  const daysLeftInCycle =
    cycleResetsAtMs === null
      ? null
      : Math.max(0, Math.ceil((cycleResetsAtMs - now.getTime()) / 86_400_000));

  /*
   * The cycle's real start date, when the API reports one.
   *
   * `cycle_started_at` removes the assumption this file used to carry: the projection previously took
   * cycle length to be 30 days because only the END date (`resets_at`) was available. With both ends
   * known, elapsed fraction is measured rather than assumed. Paired as max-with-max so the two dates
   * describe the same seat — mixing the earliest start with the latest reset would overstate the
   * cycle and understate the burn rate.
   *
   * The 30-day fallback stays for the case where the API returns no start date.
   */
  const starts = members
    .map((member) => (member.cycle_started_at ? Date.parse(member.cycle_started_at) : NaN))
    .filter((value) => !Number.isNaN(value));
  const cycleStartedAtMs = starts.length ? Math.max(...starts) : null;

  const FALLBACK_CYCLE_MS = 30 * 86_400_000;
  let projectedCycleUsd: number | null = null;
  let dailyBurnUsd: number | null = null;
  if (cycleResetsAtMs !== null && windowUsd > 0) {
    const cycleLengthMs =
      cycleStartedAtMs !== null && cycleResetsAtMs > cycleStartedAtMs
        ? cycleResetsAtMs - cycleStartedAtMs
        : FALLBACK_CYCLE_MS;
    const elapsedMs = cycleLengthMs - (cycleResetsAtMs - now.getTime());
    const elapsedFraction = elapsedMs / cycleLengthMs;
    if (elapsedFraction >= MIN_ELAPSED_FRACTION_FOR_PROJECTION && elapsedFraction <= 1) {
      projectedCycleUsd = Math.round((windowUsd / elapsedFraction) * 100) / 100;
      // Burn rate and projection are the same statement at two scales, so they come from one elapsed
      // figure — otherwise a reader could check `burn × days` against `projected` and find them
      // disagreeing.
      const elapsedDays = elapsedMs / 86_400_000;
      dailyBurnUsd = elapsedDays > 0 ? Math.round((windowUsd / elapsedDays) * 100) / 100 : null;
    }
  }

  /*
   * TWO clocks on this card, each named where it is shown.
   *
   * The hero is `windowUsd`, the selected period — what the control above says and what every
   * other tile on the tab reports. The STANDING (remaining, meter, utilisation) is the enforced
   * cycle, because that is the clock LiteLLM pauses a member on and, since backend #327, the clock
   * the backend itself reports `remaining` on, per seat, split into plan and credits.
   *
   * This card used to draw the meter from `windowUsd` too, accepting a small known gap between
   * events and LiteLLM's counter. That trade was taken when every seat's cycle was the calendar
   * month. Every paid seat is a recurring subscription now, so a seat renewing on the 17th could
   * read "40% used" here and "limit reached" in its member's banner on the same day — a bigger
   * dishonesty than the $0.02 the window figure was chosen to avoid. The backend's remaining is the
   * authority; the fallback to `budgetUsd − windowUsd` exists only for a row that cannot answer.
   */
  const roster = rosterAllowance(members);
  const cycleStanding = roster.remainingUsd !== null;
  const remainingUsd = cycleStanding ? roster.remainingUsd! : Math.max(0, budgetUsd - windowUsd);
  const standingSpentUsd = cycleStanding ? Math.max(0, budgetUsd - remainingUsd) : windowUsd;
  const utilisation = budgetUsd > 0 ? standingSpentUsd / budgetUsd : null;

  return {
    cycleUsd,
    windowUsd,
    budgetUsd,
    remainingUsd,
    standingSpentUsd,
    standingClock: cycleStanding ? 'cycle' : 'period',
    utilisation,
    planBudgetUsd: roster.planBudgetUsd,
    creditBalanceUsd: roster.creditBalanceUsd,
    creditRemainingUsd: roster.creditRemainingUsd,
    cycleResetsAt: cycleResetsAtMs === null ? null : new Date(cycleResetsAtMs).toISOString(),
    daysLeftInCycle,
    dailyBurnUsd,
    projectedCycleUsd,
    projectedOverBudget:
      projectedCycleUsd !== null && budgetUsd > 0 && projectedCycleUsd > budgetUsd,
  };
}

// ── Period comparison ───────────────────────────────────────────────────────

/**
 * Change in daily spend rate against the stretch immediately before the selected window.
 *
 * Both window totals are cumulative from today, so `larger - current` is exactly the preceding
 * stretch. Daily averages rather than totals, because that stretch is not always the same length as
 * the window (7 days vs the 8 before it) and comparing raw totals there would invent a 14% drop out
 * of arithmetic.
 */
function buildComparison(
  days: UsageWindowDays,
  report: OrgUsageReport | undefined,
  comparisonReport: OrgUsageReport | undefined,
): SpendComparison | null {
  const larger = COMPARISON_WINDOW[days];
  if (larger === null) {
    // No larger window exists to subtract from. Surfaced as a one-click suggestion rather than a
    // silent gap, so the reader learns the comparison exists at all.
    return null;
  }
  if (!report || !comparisonReport) return null;

  const currentTotal = report.org_total.spend_usd;
  const previousTotal = comparisonReport.org_total.spend_usd - currentTotal;
  const previousDays = larger - days;
  if (previousDays <= 0) return null;

  const currentDailyAvgUsd = currentTotal / days;
  // Clamped at 0: the two totals are separate snapshots, so a tiny negative from drift is possible
  // and would otherwise render as a nonsensical negative spend rate.
  const previousDailyAvgUsd = Math.max(0, previousTotal) / previousDays;

  return {
    currentDailyAvgUsd,
    previousDailyAvgUsd,
    changeRatio:
      previousDailyAvgUsd > 0
        ? (currentDailyAvgUsd - previousDailyAvgUsd) / previousDailyAvgUsd
        : null,
    previousDays,
  };
}

// ── Breakdowns ──────────────────────────────────────────────────────────────

function toServiceRows(serviceReport: OrgServiceUsageReport | undefined): ServiceMetricRow[] {
  if (!serviceReport) return [];
  const totalCost = serviceReport.by_service.reduce((sum, row) => sum + row.cost_usd, 0);
  return serviceReport.by_service.map((row) => ({
    service: row.service,
    label: labelForService(row.service),
    costUsd: row.cost_usd,
    shareOfTotal: safeShare(row.cost_usd, totalCost),
    runs: row.run_count,
    failed: row.failed_count,
    failureRate: rate(row.failed_count, row.run_count),
    inputTokens: row.input_tokens,
    outputTokens: row.output_tokens,
    avgLatencyMs: row.avg_latency_ms,
  }));
}

function toPersonRows(report: OrgUsageReport | undefined): PersonSpendRow[] {
  if (!report) return [];
  const totalWindow = report.members.reduce((sum, member) => sum + member.spend_usd_window, 0);
  return report.members.map((member) => {
    const cycleUsd = member.spend_usd_cycle;
    const budgetUsd = member.budget_usd;
    const hasBudget = budgetUsd > 0;
    return {
      userId: member.user_id,
      label: member.email ?? member.user_id,
      windowUsd: member.spend_usd_window,
      shareOfTotal: safeShare(member.spend_usd_window, totalWindow),
      cycleUsd,
      budgetUsd,
      withinBudgetUsd: hasBudget ? Math.min(cycleUsd, budgetUsd) : cycleUsd,
      overageUsd: hasBudget ? Math.max(0, cycleUsd - budgetUsd) : 0,
      utilisation: hasBudget ? cycleUsd / budgetUsd : null,
      blocked: member.blocked,
      status: member.status,
    };
  });
}

// ── Attention ───────────────────────────────────────────────────────────────

/**
 * The synthesis step: what an admin should actually look at, in priority order.
 *
 * Deliberately short and specific. A list of twelve mild observations is noise; naming the two things
 * that are wrong is the whole value. Ordered critical-first, and capped — the charts below carry the
 * long tail.
 */
function buildAttention(
  people: PersonSpendRow[],
  services: ServiceMetricRow[],
  spend: SpendSummary,
): AttentionItem[] {
  const items: AttentionItem[] = [];
  const money = (value: number) =>
    value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
  const percent = (value: number) => `${Math.round(value * 100)}%`;

  /*
   * Groups of the same problem are stated once, as a total.
   *
   * Naming every affected seat individually turned a five-seat outage into five near-identical lines
   * that differed only by address — which reads as a log, not as a briefing, and pushed everything
   * else out of the visible list. Past this count the aggregate carries more information per line
   * than the roll call does, and the "Review seats" link is right there for the names.
   */
  const AGGREGATE_ABOVE = 2;

  const listOrAggregate = (
    group: PersonSpendRow[],
    idPrefix: string,
    severity: AttentionItem['severity'],
    one: (person: PersonSpendRow) => string,
    many: (count: number, total: number) => string,
    /** Set only on the "already cut off" group — lets the callout give that case a louder
     *  treatment than the rest of `critical`, without a second component to keep in sync. */
    blocking?: true,
  ) => {
    if (group.length === 0) return;
    if (group.length <= AGGREGATE_ABOVE) {
      for (const person of group) {
        items.push({ id: `${idPrefix}:${person.userId}`, severity, message: one(person), blocking });
      }
      return;
    }
    items.push({
      id: `${idPrefix}:aggregate`,
      severity,
      message: many(
        group.length,
        group.reduce((sum, person) => sum + person.cycleUsd, 0),
      ),
      blocking,
    });
  };

  // 1. Already cut off — the only state where someone is actively unable to work.
  listOrAggregate(
    people.filter((p) => p.blocked),
    'blocked',
    'critical',
    (person) =>
      `${person.label} is blocked — ${money(person.cycleUsd)} spent against a ${money(person.budgetUsd)} budget.`,
    (count, total) =>
      `${count} members are blocked, having spent ${money(total)} against their budgets. They cannot run anything until their budgets are raised or the cycle resets.`,
    true,
  );

  // 2. Over budget but not yet blocked.
  listOrAggregate(
    people.filter((p) => !p.blocked && p.utilisation !== null && p.utilisation >= 1),
    'over',
    'critical',
    (person) =>
      `${person.label} is over budget at ${percent(person.utilisation!)} of ${money(person.budgetUsd)}.`,
    (count, total) =>
      `${count} members are over budget, ${money(total)} spent between them this cycle.`,
  );

  // 3. The org as a whole heading for its limit before the cycle resets.
  if (spend.projectedOverBudget && spend.projectedCycleUsd !== null) {
    items.push({
      id: 'projected-over',
      severity: 'warning',
      message: `At the current rate this cycle will reach ${money(spend.projectedCycleUsd)} against a ${money(spend.budgetUsd)} budget.`,
    });
  }

  // 4. Individual seats approaching their limit.
  listOrAggregate(
    people.filter(
      (p) =>
        !p.blocked &&
        p.utilisation !== null &&
        p.utilisation >= NEAR_LIMIT_UTILISATION &&
        p.utilisation < 1,
    ),
    'near',
    'warning',
    (person) => `${person.label} has used ${percent(person.utilisation!)} of their budget.`,
    (count) => `${count} members have used over ${percent(NEAR_LIMIT_UTILISATION)} of their budget.`,
  );

  // 5. Concentration risk — one service being most of the bill is a cost-control lead, not a fault.
  const dominant = services.find((service) => service.shareOfTotal >= DOMINANT_SERVICE_SHARE);
  if (dominant) {
    items.push({
      id: `dominant:${dominant.service}`,
      severity: 'warning',
      message: `${dominant.label} accounts for ${percent(dominant.shareOfTotal)} of all service spend.`,
    });
  }

  return items;
}

/**
 * The spend distribution, in deciles, highest first.
 *
 * Answers "is this bill one person?" without making the reader do arithmetic down a per-member
 * table — which is why it belongs beside that table rather than on a screen that does not list the
 * people it distributes over. Moved here from `buildOverview` with the card.
 *
 * Rank-proportional assignment rather than true percentiles, so a 9-seat org fills nine buckets
 * instead of crushing everyone into the first one.
 */
function buildConcentration(people: PersonSpendRow[]): SpendDecile[] {
  const spenders = people.filter((person) => person.windowUsd > 0);
  if (spenders.length === 0) return [];

  const sorted = [...spenders].sort((a, b) => b.windowUsd - a.windowUsd);
  const total = sorted.reduce((sum, person) => sum + person.windowUsd, 0);

  const buckets: SpendDecile[] = Array.from({ length: 10 }, (_, decile) => ({
    decile,
    shareOfTotal: 0,
    members: 0,
  }));

  sorted.forEach((person, index) => {
    const decile = Math.min(9, Math.floor((index / sorted.length) * 10));
    buckets[decile].shareOfTotal += total > 0 ? person.windowUsd / total : 0;
    buckets[decile].members += 1;
  });

  return buckets;
}

/** Days of trailing actuals the month-end projection extrapolates from. */
const PROJECTION_RUN_RATE_DAYS = 7;

/**
 * Cumulative spend across the window, with the tail extrapolated to the end of the calendar month.
 *
 * Returns `null` without a real per-day series. That is the whole point: no endpoint bucketed by day
 * until `GET /usage/timeseries` shipped, and the card's job when the series is missing is to say so,
 * not to draw a straight line between two aggregates and let it read as history.
 */
function buildCumulative(
  timeseries: OrgUsageTimeseries | undefined,
  now: Date,
  /**
   * Whether the selected range is still open.
   *
   * From `dev`. A closed month — viewing "last month" — is finished, so extrapolating a trailing run
   * rate toward a month end that has already passed would draw a forecast of the past. Its series is
   * pure actuals, and the card's description must not claim a projection either.
   */
  isOpenRange: boolean,
): CumulativeSpendPoint[] | null {
  const points = timeseries?.points ?? [];
  if (points.length === 0) return null;

  const sorted = [...points].sort((a, b) => a.date.localeCompare(b.date));
  const actual: CumulativeSpendPoint[] = [];
  let running = 0;
  for (const point of sorted) {
    running += point.spend_usd;
    actual.push({ date: point.date, cumulativeUsd: Math.round(running * 100) / 100, projected: false });
  }

  /*
   * Projection: the trailing run rate carried to month end.
   *
   * The trailing window is used rather than the whole series because an org that started using
   * nCoder halfway through the window has a lifetime average that badly understates where it is
   * heading — and the month-end figure is the one someone budgets against.
   */
  // Closed range: actuals only, no projected tail.
  if (!isOpenRange) return actual;

  const tail = sorted.slice(-PROJECTION_RUN_RATE_DAYS);
  const dailyRate = tail.reduce((sum, point) => sum + point.spend_usd, 0) / (tail.length || 1);

  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const last = actual[actual.length - 1];
  // `T00:00:00` keeps the date local; a bare `YYYY-MM-DD` parses as UTC and lands a day early for
  // readers west of Greenwich — the same trap `TrendAreaChart.parseDay` documents.
  const cursor = new Date(`${last.date}T00:00:00`);

  const projected: CumulativeSpendPoint[] = [];
  let projectedTotal = last.cumulativeUsd;
  while (cursor < monthEnd) {
    cursor.setDate(cursor.getDate() + 1);
    projectedTotal += dailyRate;
    projected.push({
      date: `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`,
      cumulativeUsd: Math.round(projectedTotal * 100) / 100,
      projected: true,
    });
  }

  return [...actual, ...projected];
}

/** Human label for a calendar-month selection, matching the control's own option labels. */
function monthRangeLabel(month: Exclude<UsageWindowSelection, number>): string {
  if (month === 'this_quarter') return 'this quarter';
  return month === 'current' ? 'this month' : 'last month';
}

// ── Root ────────────────────────────────────────────────────────────────────

export interface BuildUsageAnalysisInput {
  /**
   * The selected range — a day window OR a calendar month.
   *
   * Months arrived with `dev`'s move of the Usage tab off days mode. Everything this adapter derives
   * is range-agnostic (spend, byService, byPerson, concentration, cumulative, trend, attention) — it
   * reads whatever the report and service report cover. The ONE exception is the period-over-period
   * delta, which is built by subtracting nested day windows and has no month equivalent here; for a
   * month selection `comparison` is therefore `null`, and `SpendSummaryPanel` already states that
   * absence in words rather than showing a dash.
   */
  selection: UsageWindowSelection;
  report: OrgUsageReport | undefined;
  /** The next-larger window, used only to derive the preceding period. */
  comparisonReport: OrgUsageReport | undefined;
  serviceReport: OrgServiceUsageReport | undefined;
  /**
   * The real per-day series, from `GET /usage/timeseries`.
   *
   * `undefined` while the request is in flight, or when it failed — both leave `trend` null, and the
   * card says so. This replaced a local generator that fabricated a plausible series behind a
   * production build gate; there is now nothing in this module tree that invents a number.
   */
  timeseries: OrgUsageTimeseries | undefined;
  orgId: string | null;
  /** Injected rather than read from the clock, so the result is pure and screenshot-stable. */
  now: Date;
}

export function buildUsageAnalysis({
  selection,
  report,
  comparisonReport,
  serviceReport,
  timeseries,
  now,
}: BuildUsageAnalysisInput): UsageAnalysis {
  const isMonth = isMonthSelection(selection);
  const spend = buildSpendSummary(report, now);
  const byService = toServiceRows(serviceReport);
  const byPerson = toPersonRows(report);
  const comparison = isMonth ? null : buildComparison(selection, report, comparisonReport);

  const tokens = report
    ? report.org_total.prompt_tokens + report.org_total.completion_tokens
    : 0;

  /*
   * The real per-day series.
   *
   * `null` rather than an empty array when there is nothing to plot, because the card distinguishes
   * "no series available" from "a series of zeroes" and only the first is true here: the endpoint is
   * sparse, so a quiet org returns no points rather than a run of zeroed days.
   *
   * Spend comes from the LiteLLM spend logs and runs from observability — the same two-source split
   * every other figure in this adapter honours. The endpoint does the joining; nothing is summed
   * across the two here.
   */
  const trend: UsageAnalysis['trend'] =
    timeseries && timeseries.points.length > 0
      ? timeseries.points.map((point) => ({
          date: point.date,
          spendUsd: point.spend_usd,
          tokens: point.input_tokens + point.output_tokens,
          runs: point.run_count,
        }))
      : null;

  return {
    window: {
      selection,
      label: isMonth ? monthRangeLabel(selection) : `last ${selection} days`,
    },
    spend,
    /*
     * A month selection gets a plain `null`: no delta, and no "switch to a window where one works"
     * suggestion either, because there is no such window in month mode. The summary renders the
     * absence as a sentence — see the comment on `selection` above.
     */
    comparison: isMonth
      ? null
      : (comparison ??
        (COMPARISON_WINDOW[selection] === null
          ? ({
              currentDailyAvgUsd: spend.windowUsd / selection,
              previousDailyAvgUsd: 0,
              changeRatio: null,
              previousDays: 0,
              suggestedWindow: 15,
            } satisfies SpendComparison)
          : null)),
    attention: buildAttention(byPerson, byService, spend),
    concentration: buildConcentration(byPerson),
    /*
     * `previous` is the only selection that is definitively CLOSED. A day window and the current
     * month are both still accruing, so both get the projected tail.
     */
    cumulative: buildCumulative(timeseries, now, selection !== 'previous'),
    reliability: {
      runs: serviceReport?.run_count ?? 0,
      failureRate: serviceReport ? rate(serviceReport.failed_count, serviceReport.run_count) : null,
      avgLatencyMs: serviceReport?.avg_latency_ms ?? null,
    },
    tokens,
    trend,
    byService,
    byPerson,
    provenance: {
      // Both upstreams can degrade independently, so the trend's own partial sources are merged in
      // rather than dropped — a zeroed day looks identical to a quiet one without this.
      partialSources: [
        ...new Set([...(report?.partial ?? []), ...(timeseries?.partial ?? [])]),
      ],
    },
  };
}
